import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { SwitchInlineInput } from "@/components/ui/forms/switchInlineInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { useStripeAccount } from "@/modules/finances/hooks/useStripeAccount"
import { cn } from "@/modules/lib/utils"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { AlertIconDark, AlertIconLight } from "@images/svgIcons/alertIcon"
import { word } from "@modules/core/utils/i18n"
import { currencySymbol, formatCurrency } from "@modules/currency/currency"
import { FinanceFeesAlertCard } from "@modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { type ProgramBundleFormPageProps } from "@modules/programBundle/components/programBundleForm"
import {
  OnBoardingStripeModal,
  type OnBoardingStripeModalRef,
} from "@modules/programs/components/onBoardingStripeModal"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { ArrowLeft } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export const ProgramBundleFormThirdPage = ({
  formik,
  isFormFieldInValid,
  getFormErrorMessage,
  handleSubmit,
  setPageNumber,
  isSubmitting,
  className,
  isEdited,
}: ProgramBundleFormPageProps) => {
  const onBoardingStripeModalRef = useRef<OnBoardingStripeModalRef>(null)
  const userPrograms = usePrograms({ programFilter: ProgramFilter.OWNED })
  const filterPrograms = userPrograms.data.content.filter((program) => formik.values.programIds.includes(program.id))
  const user = useUser()
  const language = useLanguage()
  const sumOfSelectedProgramPrices = filterPrograms.reduce((acc, program) => {
    return acc + (program.price?.value ?? 0)
  }, 0)
  const { theme } = useTheme()
  const { updateStripeAccount } = useStripeAccount()

  const enum programBundlePice {
    FREE = "FREE",
    PAID = "PAID",
  }
  const dropdownOptions = [
    { label: word("programBundle.form.pricing.free"), value: programBundlePice.FREE },
    { label: word("programBundle.form.pricing.priced"), value: programBundlePice.PAID },
  ]
  const [dropdownValue, setDropdownValue] = useState(
    formik.values.price ? programBundlePice.PAID : programBundlePice.FREE,
  )

  const handleNextStep = () => {
    const priceValue = formik.values.priceInput
    if (dropdownValue !== programBundlePice.FREE) {
      formik.setFieldValue("price", { value: priceValue, currency: user?.stripeAccountCurrency })
    }
    handleSubmit()
  }

  const setupStripeAccount = () => {
    if (
      user?.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
      user?.stripeAccountStatus === StripeAccountStatus.ONBOARDING
    )
      updateStripeAccount()
    else onBoardingStripeModalRef.current?.show()
  }

  useEffect(() => {
    if (!formik.values.addFreeTrial) formik.setFieldValue("trialDuration", null)
  }, [formik.values.addFreeTrial])

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* TITLE */}
      <div className="flex flex-col">
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("programBundle.form.thirdPage.step")}
        </Text>
        <Text fontStyle="HM" className="mt-4 text-textDefault-heading">
          {word("programBundle.form.thirdPage.title")}
        </Text>
        <Text fontStyle="BM" className="mt-1 text-textDefault-heading">
          {word("programBundle.form.thirdPage.subtitle")}
        </Text>
      </div>

      {/* PRICING */}
      <div className="mt-4 flex flex-col gap-2">
        <Text className="text-textDefault-caption" fontStyle="LS" style={{ lineHeight: "21px" }}>
          {word("programBundle.form.pricing.label").toUpperCase()}
          <Text fontStyle="LL" className="text-textError">
            {" "}
            *
          </Text>
        </Text>

        <Select
          defaultValue={dropdownValue}
          onValueChange={(value) => {
            setDropdownValue(value as programBundlePice)
            if (value === programBundlePice.FREE.toString()) {
              formik.setFieldValue("price", null)
            } else {
              formik.setFieldValue("price", { value: 0, currency: user?.stripeAccountCurrency })
            }
          }}
          items={dropdownOptions}
        >
          <SelectTrigger id={"durationType"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dropdownOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {dropdownValue === programBundlePice.PAID && (
          <>
            <StandardInput
              getFieldProps={formik.getFieldProps}
              name="priceInput"
              error={getFormErrorMessage("priceInput")}
              setFieldValue={formik.setFieldValue}
              containerClassName="flex-row"
              inputProps={{
                type: "number",
                inputMode: "numeric",
                step: 0.1,
                min: 0,
                placeholder: word("offer.form.price.placeholder", {
                  minPrice: formatCurrency(user?.stripeMinimumAmount ?? 0, user.stripeAccountCurrency, language),
                }),
                onChange: (e) => {
                  formik.setFieldValue("price", { value: e.target.value, currency: user?.stripeAccountCurrency })
                  formik.setFieldValue("priceInput", e.target.value)
                },
              }}
            >
              <Text fontStyle="TM" className="ml-1 flex w-fit items-center justify-center whitespace-nowrap">
                {`${currencySymbol(user.stripeAccountCurrency, language)} / ${word("global.month.label", { count: 1 })}`}
              </Text>
            </StandardInput>
            <FinanceFeesAlertCard />
          </>
        )}
      </div>

      <div className="card flex flex-col items-center gap-2">
        <Text fontStyle="BS" className="text-textDefault-body">
          {word("programBundle.form.pricing.sumOfSelectedProgramPrices")}
        </Text>
        <Text fontStyle="LM" className="text-textDefault-heading">
          {sumOfSelectedProgramPrices > 0
            ? formatCurrency(sumOfSelectedProgramPrices, user?.stripeAccountCurrency, language)
            : word("programBundle.form.pricing.free")}
        </Text>
      </div>

      {/* TRIAL */}
      {dropdownValue !== programBundlePice.FREE && formik.values.type === ProgramBundleType.VIRTUAL && (
        <div className="flex flex-col gap-2">
          <Text className="text-textDefault-caption" fontStyle="LS" style={{ lineHeight: "21px", marginBottom: 0 }}>
            {word("program.price.trial.label").toUpperCase()}
          </Text>
          <div className="card mt-0 flex w-full grow flex-col items-stretch gap-4 p-1! pb-5!">
            <SwitchInlineInput
              textSwitch={word("program.price.trial.inputLabel")}
              name="addFreeTrial"
              getFieldProps={formik.getFieldProps}
              error={getFormErrorMessage("addFreeTrial")}
              setFieldValue={formik.setFieldValue}
              switchContainerClassName="bg-surface-foreground"
              switchProps={{
                checked: formik.values.addFreeTrial,
              }}
            />

            <div className="mx-4">
              <StandardInput
                name="trialDuration"
                getFieldProps={formik.getFieldProps}
                error={getFormErrorMessage("trialDuration")}
                setFieldValue={formik.setFieldValue}
                containerClassName="flex-row"
                inputProps={{
                  type: "number",
                  inputMode: "numeric",
                  step: 1,
                  min: 0,
                  max: 365,
                  disabled: !formik.values.addFreeTrial,
                }}
              >
                <Text fontStyle="TM" className="ml-1 flex items-center justify-center">
                  {word("global.day.label", { count: 2 })}
                </Text>
              </StandardInput>
            </div>
          </div>
        </div>
      )}

      {/* STRIPE ACCOUNT SET UP */}
      {user?.stripeAccountStatus !== StripeAccountStatus.OK && (
        <div className="card mt-4 flex flex-col items-center gap-2">
          {theme === Theme.OwnerDark ? (
            <AlertIconDark color="currentColor" className="text-textError" />
          ) : (
            <AlertIconLight color="currentColor" className="text-textError" />
          )}

          <Text fontStyle="LL" className="text-center">
            {word("programBundle.form.stripeAccount.error.title")}
          </Text>
          <Text fontStyle="BS" className="text-center whitespace-pre-line text-textDefault-caption">
            {word("programBundle.form.stripeAccount.error.description")}
          </Text>

          <Button variant="primary" className="mt-2" onClick={setupStripeAccount}>
            {word("programBundle.form.stripeAccount.error.buttonLabel")}
          </Button>
        </div>
      )}

      {/* NEXT BUTTON */}
      <div className="mt-5 mb-6 flex w-full flex-row gap-5">
        <Button variant="outline" className="flex-1" size="lg" onClick={() => setPageNumber((prev) => prev - 1)}>
          <ArrowLeft size={18} color="currentColor" />
          {word("global.previous")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={handleNextStep}
          disabled={isSubmitting || isFormFieldInValid("priceInput") || isFormFieldInValid("trialDuration")}
        >
          {isEdited ? word("global.edit") : word("global.create")}
        </Button>
      </div>

      <OnBoardingStripeModal ref={onBoardingStripeModalRef} />
    </div>
  )
}
